// FLAG:useDocuAi — WHEN the receipt form shows the DOCUMENT's own totals (net / VAT / total as the
// invoice states them, editable, with the "the lines add up to …" hints) instead of the sum block
// that recomputes everything from the positions.
//
// Until plan omxweb-draft-receipt-instead-of-review-row T16 (2026-09-18) only the review component
// showed them. The booked expense derived from that very review fell back to the computed block,
// which rounds per line: the Ege invoice stated 1.412,88 € / 92,43 €, the form showed 1.412,89 € /
// 92,44 € and offered no way to see or correct the stated figures. A receipt that carries its
// reviewed details IS a projection of a reviewed document, so it keeps the document's totals.
//
// Flag-OFF: no host passes `documentReviewDetails` (the review entry is flag-gated), so legacy
// receipts keep the computed block byte-identical.
interface ReceiptTotalsData {
  priceNet?: number | string | null
  vatAmount?: number | string | null
  priceTotal?: number | string | null
  metaData?: {
    invoiceAnalysisSource?: string | null
    // The totals the review stamped onto the record (api: buildExpenseAnalysisFromReview →
    // updateFromAnalyzation → metaData.reviewedTotals). Part of the record itself, so it is known
    // SYNCHRONOUSLY on the first render — unlike `documentReviewDetails`, which the host fetches
    // afterwards. That order matters: the position rows fire their first change on mount, and a
    // gate that is still false at that moment lets calculateReceiptPrices overwrite the stated
    // total with Σ lines before the details ever arrive (measured 2026-09-18: expense 12518 saved
    // as 1.412,89 € after one open-and-save).
    reviewedTotals?: { priceNet?: number | null, vatAmount?: number | null, priceTotal?: number | null } | null
  } | null
}

interface ReviewDetailsLike {
  reviewSource?: { source?: string | null } | null
  [key: string]: unknown
}

// `enabled` is the STRUCTURAL flag gate, added 2026-09-18 after the omxweb review of 3ddcf86a34
// (finding F22.1): the third condition below is pure DATA, so a tenant whose useDocuAi was switched
// off still met it through records reviewed earlier — and then saw the stated block, and since T20
// its hints. „No legacy writer produces reviewedTotals" is true today but is a claim about every
// writer of record metaData, which is exactly what unbookableVatFact.ts warns against relying on.
// Callers pass useDocuAi (or review mode) through; with the flag off this is false and the computed
// block renders as it always did.
export const hasStatedReceiptTotals = ({
  enabled = true,
  isDocumentInvoiceReviewMode,
  documentReviewDetails,
  data,
}: {
  enabled?: boolean
  isDocumentInvoiceReviewMode?: boolean
  documentReviewDetails?: ReviewDetailsLike | null
  data?: ReceiptTotalsData | null
}): boolean => !!enabled
  && (!!isDocumentInvoiceReviewMode || !!documentReviewDetails || !!data?.metaData?.reviewedTotals)

// The review's own vocabulary (priceNet / vatAmount / totalAmount); totalAmount lives on the
// receipt as priceTotal.
export const buildReceiptDocumentTotals = (data?: ReceiptTotalsData | null) => ({
  priceNet: data?.priceNet,
  vatAmount: data?.vatAmount,
  totalAmount: data?.priceTotal,
})

// On an XML e-invoice the stated totals are the legally binding mapped values — shown, never
// edited (the review locks them the same way, xmlLockedFields.ts restores them server-side).
export const isXmlStatedReceipt = (
  documentReviewDetails?: ReviewDetailsLike | null,
  data?: ReceiptTotalsData | null,
): boolean => documentReviewDetails?.reviewSource?.source === 'xml'
  || data?.metaData?.invoiceAnalysisSource === 'xml'
